import { normalizeSearchTerms } from '@/storybook/utils/normalizeSearchTerms';
import { CustomColumnDef, TableOptions, isUpdateFn } from '@/types/table';
import {
  ExpandedState,
  GroupingState,
  OnChangeFn,
  PaginationState,
  Row,
  RowModel,
  RowPinningState,
  RowSelectionState,
  SortingState,
  Table,
  VisibilityState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { isValidElement, useMemo } from 'react';
import { DEFAULT_MAX_COLUMN_SIZE, DEFAULT_MIN_COLUMN_SIZE } from '../utils/constants';

const groupPrefetchedFlowOptions: {
  enableRowPinning: false;
  paginateExpandedRows: false;
} = {
  enableRowPinning: false,
  paginateExpandedRows: false,
};

const getColumnId = <T>(column: CustomColumnDef<T>): string => {
  if (column.id) {
    return column.id;
  }

  if ('accessorKey' in column && column.accessorKey) {
    return String(column.accessorKey);
  }

  return '';
};

const buildSearchableValueGetterMap = <T>(columns: CustomColumnDef<T>[]) => {
  const searchableValueGetterMap = new Map<string, NonNullable<CustomColumnDef<T>['getSearchableValue']>>();

  columns.forEach(column => {
    const columnId = getColumnId(column);

    if (columnId && column.getSearchableValue) {
      searchableValueGetterMap.set(columnId, column.getSearchableValue);
    }
  });

  return searchableValueGetterMap;
};

const getSearchableCellValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'boolean') {
    return '';
  }

  if (Array.isArray(value)) {
    return value.map(getSearchableCellValue).filter(Boolean).join(' ');
  }

  if (isValidElement<{ children?: unknown }>(value)) {
    return getSearchableCellValue(value.props.children);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'object') {
    return Object.values(value).map(getSearchableCellValue).filter(Boolean).join(' ');
  }

  return String(value);
};

interface UseTableConfigParams<T> {
  id: string;
  data: T[];
  customColumns: CustomColumnDef<T>[];
  enableColumnResizing: boolean;
  rowSelectionEnabled: boolean;
  lazyConfig: TableOptions<T>['lazy'];
  options?: TableOptions<T>;
  totalCount: number;
  sorting: SortingState;
  pagination: PaginationState;
  autoResetPageIndex?: boolean;
  globalFilter: string | undefined;
  columnVisibility: VisibilityState;
  columnOrder: string[];
  columnSizing: Record<string, number>;
  expanded: ExpandedState;
  rowSelection: RowSelectionState;
  grouping: GroupingState;
  rowPinning: RowPinningState;
  columnIdKey: string;
  containerWidth: number;
  customGroupedRowModel: (table: Table<T>) => () => RowModel<T>;
  setColumnOrder: (id: string, columnIdKey: string, order: string[]) => void;
  setColumnSizing: (id: string, columnIdKey: string, sizing: Record<string, number>) => void;
  setRowPinning: (id: string, pinning: RowPinningState) => void;
  setGrouping: (id: string, grouping: GroupingState) => void;
  setSorting: (id: string, sorting: SortingState) => void;
  setPagination: (id: string, pagination: Partial<PaginationState>) => void;
  setGlobalFilter: (filter: string | undefined) => void;
  setExpanded: OnChangeFn<ExpandedState>;
  setRowSelection: OnChangeFn<RowSelectionState>;
}

export const useTableConfig = <T>({
  id,
  data,
  customColumns,
  enableColumnResizing,
  rowSelectionEnabled,
  lazyConfig,
  options,
  totalCount,
  sorting,
  pagination,
  autoResetPageIndex = true,
  globalFilter,
  columnVisibility,
  columnOrder,
  columnSizing,
  expanded,
  rowSelection,
  grouping,
  rowPinning,
  columnIdKey,
  containerWidth,
  customGroupedRowModel,
  setColumnOrder,
  setColumnSizing,
  setRowPinning,
  setGrouping,
  setSorting,
  setPagination,
  setGlobalFilter,
  setExpanded,
  setRowSelection,
}: UseTableConfigParams<T>): Table<T> => {
  const searchableValueGetterMap = useMemo(() => buildSearchableValueGetterMap(customColumns), [customColumns]);

  const tableGlobalFilter = useMemo(() => {
    return (row: Row<T>, _columnId: string, filterValue: string): boolean => {
      const filterTerms = normalizeSearchTerms(filterValue);

      if (!filterTerms.length) {
        return true;
      }

      const searchableText = row
        .getAllCells()
        .filter(cell => cell.column.getCanGlobalFilter() && cell.column.getIsVisible())
        .map(cell => {
          const getSearchableValue = searchableValueGetterMap.get(cell.column.id);
          const searchableValue = getSearchableValue ? getSearchableValue(cell.getValue(), row.original) : '';

          return [getSearchableCellValue(cell.getValue()), getSearchableCellValue(searchableValue)]
            .filter(Boolean)
            .join(' ');
        })
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase();

      return filterTerms.every(filterTerm => searchableText.includes(filterTerm));
    };
  }, [searchableValueGetterMap]);

  return useReactTable({
    data,
    columns: customColumns,
    enableExpanding: true,
    enableColumnResizing,
    columnResizeMode: 'onChange',
    enableRowSelection: rowSelectionEnabled,
    enableMultiRowSelection: false,
    manualPagination: !!lazyConfig,
    manualSorting: options?.allowManualSortingWithLazy ? !options?.allowManualSortingWithLazy : !!lazyConfig,
    ...(lazyConfig ? { pageCount: Math.ceil(totalCount / pagination.pageSize) } : {}),
    defaultColumn: {
      size: -1,
      minSize: DEFAULT_MIN_COLUMN_SIZE,
      maxSize: DEFAULT_MAX_COLUMN_SIZE,
      enableResizing: enableColumnResizing,
    },
    ...(options?.groupingFlow === 'GroupPrefetched' && groupPrefetchedFlowOptions),
    state: {
      sorting,
      pagination,
      globalFilter,
      columnVisibility,
      columnOrder,
      columnSizing,
      expanded,
      rowSelection,
      grouping,
      ...(options?.groupingFlow !== 'GroupPrefetched' && rowPinning),
    },
    onColumnOrderChange: updater =>
      setColumnOrder(id, columnIdKey, isUpdateFn(updater) ? updater(columnOrder) : updater),
    onColumnSizingChange: updater => {
      const newSizing = isUpdateFn(updater) ? updater(columnSizing) : updater;

      const visibleCols = customColumns.filter(col => col.visible !== false);
      let total = 0;
      for (const col of visibleCols) {
        const colId = getColumnId(col);
        total += newSizing[colId] ?? col.size ?? DEFAULT_MIN_COLUMN_SIZE;
      }

      if (total < containerWidth) {
        const changedColId = Object.keys(newSizing).find(key => newSizing[key] !== columnSizing[key]);
        if (changedColId) {
          const deficit = containerWidth - total;
          newSizing[changedColId] = (newSizing[changedColId] ?? 0) + deficit;
        }
      }

      setColumnSizing(id, columnIdKey, newSizing);
    },
    onRowPinningChange: updater => setRowPinning(id, isUpdateFn(updater) ? updater(rowPinning) : updater),
    onGroupingChange: updater => setGrouping(id, isUpdateFn(updater) ? updater(grouping) : updater),
    onSortingChange: updater => setSorting(id, isUpdateFn(updater) ? updater(sorting) : updater),
    onPaginationChange: updater => setPagination(id, isUpdateFn(updater) ? updater(pagination) : updater),
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    globalFilterFn: tableGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: customGroupedRowModel,
    autoResetExpanded: false,
    autoResetPageIndex,
    groupedColumnMode: false,
  });
};
